import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import stylesPc from "./SelectGroup.module.css";
import stylesSp from "./SelectGroup_SP.module.css";
import { IsDevice } from "../../../components/function/useDeviceFlag";
import {
    FormControl,
    FilledInput,
    InputAdornment,
    createStyles,
    makeStyles,
    Theme,
    Checkbox,
    FormControlLabel,
    Chip,
    Divider,
    ThemeProvider,
    createTheme,
} from "@material-ui/core";
import Search from "@material-ui/icons/Search";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../root/RootReducer";
import { Group } from "../../../user/User";
import { GetGroupList } from "../../../user/UserActions";
import CheckedIcon from "../../../assets/image/CheckedIcon";
import { t } from "i18next";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        searchTitle: {
            fontSize: "16px",
        },
        searchInput: {
            height: "55px",
            borderRadius: "5px",
            padding: "0px 15px 20px",
            fontFamily: "noto-sans-cjk-jp",
            color: "white",
            backgroundColor: "var(--textfield-bg)",
            "&:before": {
                border: "none",
            },
            "&:hover:before": {
                border: "none",
            },
        },
        searchButton: {
            fontFamily: "noto-sans-cjk-jp,sans-serif",
            fontSize: "14px",
            height: "40px",
            width: "200px",
            marginRight: "15px",
            "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
        },
        addButton: {
            fontSize: "14px",
            borderRadius: "5px",
            "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
        },
    })
);

const theme = createTheme({
    palette: {
        secondary: {
            main: "#16ADC1",
        },
    },
});

interface SelectGroupProps {
    visibleModal: boolean;
    onClose?: () => void;
    targetName: string;
    userGroup: Group[];
    SetGroupList: (userGroup: Group[]) => void;
    SetIsSaved?: (flg: boolean) => void;
}

const SelectGroup = React.forwardRef<HTMLDivElement, SelectGroupProps>(function SelectGroup(
    props,
    ref
) {
    // レスポンシブ対応：ブラウザサイズによるCSSファイルの切り替え
    const styles = IsDevice("desktop") ? stylesPc : stylesSp;

    const { visibleModal, onClose, targetName, SetGroupList, userGroup, SetIsSaved } = props;
    const classes = useStyles();
    const dispatch = useDispatch();

    const { groupList } = useSelector((state: AppState) => ({
        groupList: state.user.groupList,
    }));

    const [displayGroupList, ChangeDisplayGroupList] = React.useState<string[]>([]);
    const [searchKey, setSearchKey] = React.useState("");
    const [editGroup, ChangeEditGroup] = useState<Group[]>(userGroup);

    const InputSearchGroup = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKey(event.currentTarget.value.trim());
    };

    useEffect(() => {
        if (groupList.length === 0 && visibleModal) {
            dispatch(GetGroupList());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleModal]);

    // 検索していないときはすべてのグループを表示する、検索時はフィルター結果のみ表示する
    useEffect(() => {
        const groupNameList = groupList?.map(function (row) {
            return row.title;
        });
        const searchGroup = groupNameList.filter(
            (groupName) => groupName.indexOf(searchKey) !== -1
        );
        ChangeDisplayGroupList(
            searchGroup.length !== 0 || searchKey.length !== 0 ? searchGroup : groupNameList
        );
    }, [groupList, searchKey]);

    const DeleteGroup = (deleteGroupName: string) => () => {
        ChangeEditGroup(editGroup.filter((v) => v.title !== deleteGroupName));
    };

    const CheckGroup = (event: React.ChangeEvent<HTMLInputElement>) => {
        let tempList = editGroup;
        if (!event.currentTarget.checked) {
            tempList = tempList.filter((v) => v.title !== event.currentTarget.name);
        } else {
            const addGroup = groupList.filter((v) => v.title === event.currentTarget.name);
            if (tempList && tempList.length > 0) {
                tempList = tempList.concat(addGroup);
            } else {
                tempList = addGroup;
            }
        }
        ChangeEditGroup(tempList);
    };

    const isChecked = (item: string) => {
        if (editGroup && editGroup.length > 0) {
            const element = editGroup.find((el) => el.title === item);
            if (element) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    };

    // 編集完了
    const ConfirmGroup = () => {
        if (onClose) {
            SetGroupList(editGroup);
            if (SetIsSaved) {
                SetIsSaved(false);
            }
            onClose();
        }
    };

    const handleCancel = () => {
        if (onClose) onClose();
    };

    return (
        <>
            <ThemeProvider theme={theme}>
                {!IsDevice("desktop") && (
                    <div onClick={onClose} className={styles.closeButton}>
                        <img
                            src={require("../../../assets/image/closeFilter.svg").default}
                            alt="close"
                        />
                    </div>
                )}
                <div className={styles.root}>
                    <div className={styles.conditionArea}>
                        <div
                            style={{
                                display: "block",
                                marginBottom: "15px",
                                color: "var(--white)",
                            }}
                        >
                            <h2>対象: {targetName}</h2>
                            <h2>
                                所属グループ数:{" "}
                                {editGroup && editGroup.length > 0 ? editGroup.length : 0}
                            </h2>
                            <div style={{ display: "flex", flexWrap: "wrap" }}>
                                {editGroup &&
                                    editGroup.length > 0 &&
                                    editGroup.map((group) => (
                                        <div
                                            key={group.id}
                                            style={{ marginRight: "7px", marginTop: "5px" }}
                                        >
                                            <Chip
                                                label={group.title}
                                                color="secondary"
                                                style={{
                                                    color: "var(--white)",
                                                    backgroundColor: "#333A43",
                                                }}
                                                size="medium"
                                                className={styles.tag}
                                                onDelete={DeleteGroup(group.title)}
                                            />
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles.skillArea}>
                        <Divider variant="middle" />
                        <h2>グループの追加</h2>
                        <h2>■登録されているグループから追加</h2>
                        <FormControl fullWidth variant="filled" color="secondary">
                            <FilledInput
                                id="searchInput"
                                onChange={InputSearchGroup}
                                placeholder="検索したいグループを入力"
                                endAdornment={
                                    <InputAdornment position="end">
                                        <Search
                                            color="secondary"
                                            style={{
                                                color: "var(--white)",
                                                marginTop: "20px",
                                            }}
                                        />
                                    </InputAdornment>
                                }
                                autoComplete={"off"}
                                className={classes.searchInput}
                            />
                        </FormControl>
                        <div className={styles.skillScrollArea}>
                            {displayGroupList &&
                                displayGroupList.map((item, index) => (
                                    <FormControlLabel
                                        key={index}
                                        control={
                                            <Checkbox
                                                checkedIcon={
                                                    <div className={styles.checkedIcon}>
                                                        <CheckedIcon />
                                                    </div>
                                                }
                                                name={item}
                                                checked={isChecked(item)}
                                                onChange={CheckGroup}
                                                style={{ color: "var(--white)" }}
                                            />
                                        }
                                        label={item}
                                        className={styles.skillCheckBox}
                                    />
                                ))}
                        </div>
                    </div>
                    <div className={styles.btnArea}>
                        <Button
                            variant="outlined"
                            type="submit"
                            className={classes.searchButton}
                            onClick={handleCancel}
                            style={{
                                marginRight: "10px",
                                color: "#9C9C9C",
                                fontSize: "16px",
                                fontFamily: "noto-sans-cjk-jp,sans-serif",
                                border: "solid 2px #5D5D5D",
                            }}
                        >
                            {t("キャンセル")}
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            className={classes.searchButton}
                            onClick={ConfirmGroup}
                            disabled={editGroup === props.userGroup}
                            style={{
                                fontSize: "16px",
                                backgroundColor:
                                    editGroup === props.userGroup
                                        ? "var(--button-bg)"
                                        : "var(--studio-btn-bg)",
                                color:
                                    editGroup === props.userGroup
                                        ? "var(--loading-bg)"
                                        : "var(--white)",
                            }}
                        >
                            {t("更新")}
                        </Button>
                    </div>
                </div>
            </ThemeProvider>
        </>
    );
});
export default SelectGroup;
